import type { ListResponse, LoginResponse, SessionState, UploadResult, UserId } from '../types';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseError(response: Response): Promise<never> {
  let message = `Request failed (${response.status})`;
  try {
    const body = (await response.json()) as { error?: string };
    if (body && typeof body.error === 'string') message = body.error;
  } catch {
    // ignore parse errors — fall through with default message
  }
  throw new ApiError(response.status, message);
}

export async function fetchSession(): Promise<SessionState> {
  const response = await fetch('/api/session', {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) await parseError(response);
  return (await response.json()) as SessionState;
}

export async function login(user: UserId, credential: string): Promise<LoginResponse> {
  const response = await fetch('/api/login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ user, credential }),
  });
  if (!response.ok) await parseError(response);
  return (await response.json()) as LoginResponse;
}

export async function logout(): Promise<void> {
  const response = await fetch('/api/logout', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!response.ok) await parseError(response);
}

export interface UploadParams {
  file: File;
  prefix: string;
  filename?: string;
  overwrite?: boolean;
  onProgress?: (sentBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
}

export function uploadFile(params: UploadParams): Promise<UploadResult> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append('file', params.file);
    form.append('prefix', params.prefix);
    if (params.filename && params.filename.trim()) form.append('filename', params.filename.trim());
    if (params.overwrite) form.append('overwrite', 'true');

    xhr.open('POST', '/api/upload');
    xhr.responseType = 'json';
    xhr.withCredentials = true;

    if (params.onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) params.onProgress!(event.loaded, event.total);
      });
    }

    xhr.addEventListener('load', () => {
      const status = xhr.status;
      const body = xhr.response as UploadResult | { error?: string } | null;
      if (status >= 200 && status < 300 && body && 'ok' in body) {
        resolve(body as UploadResult);
        return;
      }
      const message =
        body && typeof (body as { error?: string }).error === 'string'
          ? (body as { error: string }).error
          : `Upload failed (${status})`;
      reject(new ApiError(status, message));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'Network error during upload.')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'Upload cancelled.')));

    if (params.signal) {
      if (params.signal.aborted) {
        xhr.abort();
        reject(new ApiError(0, 'Upload cancelled.'));
        return;
      }
      params.signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(form);
  });
}

export interface ListParams {
  prefix?: string;
  kind?: string;
}

export async function listObjects(params: ListParams = {}): Promise<ListResponse> {
  const url = new URL('/api/list', window.location.origin);
  if (params.prefix !== undefined) url.searchParams.set('prefix', params.prefix);
  if (params.kind) url.searchParams.set('kind', params.kind);
  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'same-origin',
    headers: { accept: 'application/json' },
  });
  if (!response.ok) await parseError(response);
  return (await response.json()) as ListResponse;
}

export async function deleteObject(key: string): Promise<void> {
  const url = new URL('/api/object', window.location.origin);
  url.searchParams.set('key', key);
  const response = await fetch(url.toString(), {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!response.ok) await parseError(response);
}

export interface RebuildResult {
  ok: true;
  user: UserId;
  total: number;
  updatedAt: string;
}

export async function rebuildManifest(): Promise<RebuildResult> {
  const response = await fetch('/api/rebuild-manifest', {
    method: 'POST',
    credentials: 'same-origin',
  });
  if (!response.ok) await parseError(response);
  return (await response.json()) as RebuildResult;
}
